import React, { useEffect, useRef } from "react";

// Leaflet ships its marker icons via CSS background images that webpack
// resolves at runtime. The shadcn project doesn't expose those assets by
// default, so we point them to the official CDN once at module load.
import L from "leaflet";
import "leaflet/dist/leaflet.css";

if (!L._iconUrlPatched) {
  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  });
  L._iconUrlPatched = true;
}

const ICONS = {
  blue: new L.Icon({
    iconUrl: "https://unpkg.com/leaflet-color-markers@1.0.0/img/marker-icon-blue.png",
    iconRetinaUrl: "https://unpkg.com/leaflet-color-markers@1.0.0/img/marker-icon-2x-blue.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
    iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
  }),
  green: new L.Icon({
    iconUrl: "https://unpkg.com/leaflet-color-markers@1.0.0/img/marker-icon-green.png",
    iconRetinaUrl: "https://unpkg.com/leaflet-color-markers@1.0.0/img/marker-icon-2x-green.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
    iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
  }),
};

/**
 * CheckInMap — renders a small OpenStreetMap with two markers: the check-in
 * location (blue) and the booking address (green). If only one of the two
 * coordinates is available we just show that one.
 *
 * Props: { checkin: {lat,lng} | null, address: {lat,lng} | null }
 *
 * We use the imperative Leaflet API instead of react-leaflet so we don't have
 * to fight context/SSR issues; the map only lives inside this leaf component.
 */
export default function CheckInMap({ checkin, address }) {
  const mapRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return undefined;
    const points = [];
    if (checkin?.lat != null && checkin?.lng != null) points.push([checkin.lat, checkin.lng, "checkin"]);
    if (address?.lat != null && address?.lng != null) points.push([address.lat, address.lng, "address"]);
    if (points.length === 0) return undefined;

    // Lazy-create the map on first paint.
    if (!mapRef.current) {
      mapRef.current = L.map(containerRef.current, { scrollWheelZoom: false, zoomControl: true });
      L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "&copy; OpenStreetMap contributors",
        maxZoom: 19,
      }).addTo(mapRef.current);
    }

    const map = mapRef.current;
    // Clear previous markers/polylines
    map.eachLayer((layer) => {
      if (layer instanceof L.Marker || layer instanceof L.Polyline) map.removeLayer(layer);
    });

    const latlngs = [];
    points.forEach(([lat, lng, kind]) => {
      const icon = kind === "address" ? ICONS.green : ICONS.blue;
      L.marker([lat, lng], { icon }).addTo(map).bindPopup(kind === "address" ? "Booking address" : "Cleaner check-in");
      latlngs.push([lat, lng]);
    });
    if (latlngs.length === 2) {
      L.polyline(latlngs, { color: "#0EA5A4", weight: 3, dashArray: "6, 8" }).addTo(map);
      map.fitBounds(latlngs, { padding: [40, 40] });
    } else {
      map.setView(latlngs[0], 15);
    }
    // Make sure tiles render after layout settles
    setTimeout(() => map.invalidateSize(), 100);
    return undefined;
  }, [checkin?.lat, checkin?.lng, address?.lat, address?.lng]);

  useEffect(() => () => {
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }
  }, []);

  const hasAny = (checkin?.lat != null && checkin?.lng != null) || (address?.lat != null && address?.lng != null);
  if (!hasAny) {
    return (
      <div className="rounded-xl bg-[#F5F5F4] border border-[#E5E7EB] py-10 text-center text-sm text-[#9CA3AF]" data-testid="checkin-map-empty">
        Address could not be geocoded and no check-in coordinates are available.
      </div>
    );
  }
  return <div ref={containerRef} className="h-72 w-full rounded-xl overflow-hidden border border-[#E5E7EB]" data-testid="checkin-map" />;
}
